/**
 * Current Area Query Hook
 *
 * Manages the global "current area" state using React Query.
 * This represents the user's active/selected area that persists across navigation.
 *
 * Usage:
 * - Components: Use `useCurrentArea()` to read the current area
 * - Search/Navigation: Use `useSetCurrentArea()` to update when user selects a new area
 *
 * The current area is stored in React Query cache at ['area', 'current'] and
 * automatically triggers re-renders in all subscribed components.
 */

import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import type { AreaResponse } from '@/src/lib/api';

/**
 * Query key for the current/active area
 */
export const currentAreaKeys = {
  current: ['area', 'current'] as const,
  nearMe: ['area', 'nearMe'] as const,
};

/**
 * Hook to read the current area from React Query cache
 *
 * Returns the user's active/selected area that persists across navigation.
 * Components using this hook will automatically re-render when the current area changes.
 *
 * @example
 * ```tsx
 * function HeaderNav() {
 *   const { data: currentArea } = useCurrentArea();
 *   return <Link href={`/${currentArea?.resourceId}/apartments-for-rent`}>Apartments</Link>;
 * }
 * ```
 */
export const useCurrentArea = () => {
  return useQuery<AreaResponse>({
    queryKey: currentAreaKeys.current,
    queryFn: () => {
      // This should never be called - we only read from cache
      throw new Error('Current area should be set via setCurrentArea');
    },
    enabled: false, // Don't auto-fetch, only read from cache
    staleTime: Infinity, // Current area doesn't expire
  });
};

/**
 * Hook to update the current area in React Query cache
 *
 * Call this when:
 * - User searches for a new area
 * - User navigates to an area page
 * - User lands on homepage (set to IP geolocation area)
 *
 * @returns Function to update the current area
 *
 * @example
 * ```tsx
 * function SearchResults() {
 *   const setCurrentArea = useSetCurrentArea();
 *   const { data: area } = useAreaByResourceId(resourceId);
 *
 *   useEffect(() => {
 *     if (area) {
 *       setCurrentArea(area);
 *     }
 *   }, [area, setCurrentArea]);
 * }
 * ```
 */
export const useSetCurrentArea = () => {
  const queryClient = useQueryClient();

  return (area: AreaResponse) => {
    queryClient.setQueryData(currentAreaKeys.current, area);
  };
};

/**
 * Hook to read the IP-based "Near Me" area from React Query cache.
 * Unlike currentArea, this is set once from IP geolocation and never
 * overwritten by navigation, so it always reflects the user's actual location.
 */
export const useNearMeArea = () => {
  return useQuery<AreaResponse>({
    queryKey: currentAreaKeys.nearMe,
    queryFn: () => {
      throw new Error('Near Me area should be set via useSetNearMeArea');
    },
    enabled: false,
    staleTime: Infinity,
  });
};

export const useSetNearMeArea = () => {
  const queryClient = useQueryClient();

  return (area: AreaResponse) => {
    queryClient.setQueryData(currentAreaKeys.nearMe, area);
  };
};
